<template>
    <sui-segment :loading="isLoading">
        <div>
            <div class="float-right">
                <sui-button
                    @click="onClickDownloadFile"
                    icon="download"
                    label-position="left"
                    :disabled="monitoringInfos && monitoringInfos.gateway.gatewayConfig.fetchMethod === 'LOCAL_FTP'"
                >
                    {{ $t('gateway.processes.downloadFile') }}
                </sui-button>

                <sui-button
                    @click="onClickSelectFile"
                    :disabled="hasCurrentRunningProcess || isUploadingFile"
                    :loading="isUploadingFile"
                    icon="upload"
                    label-position="left"
                >
                    {{ $t('gateway.processes.manual') }}
                    <input type="file" ref="inputFile" style="display: none;" @change="onSelectUploadFile" />
                </sui-button>


                <sui-popup
                    basic
                    :content="$t('gateway.processes.abort')"
                >
                  <sui-button icon="stop circle outline" negative  slot="trigger" @click="onClickAbort"/>
                </sui-popup>
            </div>

            <div v-if="monitoringInfos">
                <h1 class="hoverable-message">
                    <span title="Accéder à la passerelle" @click="openGateway(monitoringInfos.gateway.id)">{{ $t('gateway.processes.monitoTitleGateway', { gatewayName: monitoringInfos.gateway.name }) }}</span>
                    &nbsp;
                    <span title="Accéder à l'agence" @click="openCustomer(customerId)">{{ $t('gateway.processes.monitoTitleCompany', { customerName: getCustomer(customerId).name }) }}</span>
                </h1>
                <sui-message
                    color="violet"
                    v-if="isSharedGatewayFile"
                >
                    <sui-message-header>
                        <span title="Accéder à l'agence" class="hoverable-message" v-on:click="goToCustomerParentGateway(monitoringInfos.customer.id)">{{ $t('gateway.processes.sharedBy', { name: monitoringInfos.customer.name }) }}</span>
                    </sui-message-header>
                </sui-message>
            </div>

            <sui-segment v-if="latestSynchroStats">
                <div class="ui clearing basic header">
                    <strong>{{ $t('gateway.synchro.status') }}</strong>
                    <sui-button
                            class="right floated"
                            :loading="isSynchingToOdyssey"
                            @click="syncToOdyssey()"
                    >
                        {{ $t('gateway.synchro.reSync') }}
                    </sui-button>
                </div>
                <div class="ui very relaxed horizontal list">
                    <div class="item">
                        <strong>{{ $t('gateway.synchro.processId') }}:</strong> {{ this.getLatestSuccessfulGatewayProcessId()}}
                    </div>

                    <div class="item hoverable-message" :title="getDate(latestSynchroStats.latestUpdatedAt)">
                        <strong>{{ $t('gateway.synchro.date') }}:</strong> {{ getFromNow(latestSynchroStats.latestUpdatedAt) }}
                    </div>

                    <div class="item">
                        <strong>{{ $t('gateway.synchro.nbListings') }}:</strong> {{ latestSynchroStats.countDistinctListing }}
                    </div>
                </div>
                <br/>
                <strong>{{ $t('gateway.synchro.companies') }}: </strong>
                <span v-if="customerWithSynchedListings.length === 0">–</span>
                <span v-else>
                    <strong
                            v-for="(customer, index) in customerWithSynchedListings"
                            :key="customer.id"
                            class="hoverable-message"
                            @click="goToCustomerParentGateway(customer.id)"
                    >
                        {{ customer.name }}<span v-if="index < customerWithSynchedListings.length - 1">  •  </span>
                    </strong>
                </span>
            </sui-segment>

            <sui-segment v-if="lastProcessWithWarnings">
                <sui-message warning>
                    <sui-message-header>
                        {{ $t('common.warning') }}
                        <span class="muted">
                            #{{ lastProcessWithWarnings.id }}
                          </span>
                    </sui-message-header>

                    <sui-list bulleted>
                        <sui-list-item
                                v-for="(w, i) in lastProcessWithWarnings.warning"
                                :key="`${lastProcessWithWarnings.id}-w-${i}`"
                        >
                            {{ w }}
                        </sui-list-item>
                    </sui-list>
                </sui-message>
            </sui-segment>

            <sui-segment v-if="lastProcessSkipDate">
                <sui-message info>
                    <span title="Last skipped process date">{{ $t('gateway.processes.lastSkipDate', { date: lastProcessSkipDate }) }}</span><br/>
                    <span title="more" style="font-style: italic">{{ $t('gateway.processes.lastSkipDateMore') }}</span>
                </sui-message>
            </sui-segment>
        </div>

       <sui-table class="compact" celled striped>
            <sui-table-header>
                <sui-table-row>
                    <sui-table-header-cell :width="2">{{ $t('common.state') }}</sui-table-header-cell>
                    <sui-table-header-cell :width="1">{{ $t('common.id') }}</sui-table-header-cell>
                    <sui-table-header-cell :width="1">{{ $t('common.id') }} Redis</sui-table-header-cell>
                    <sui-table-header-cell :width="2">{{ $t('gateway.processes.adsDownloaded') }}</sui-table-header-cell>
                    <sui-table-header-cell :width="2">{{ $t('common.startedAt') }}</sui-table-header-cell>
                    <sui-table-header-cell :width="2">{{ $t('common.endedAt') }}</sui-table-header-cell>
                    <sui-table-header-cell :width="1">{{ $t('common.duration') }}</sui-table-header-cell>
                    <sui-table-header-cell :width="2">{{ $t('common.origin') }}</sui-table-header-cell>
                    <sui-table-header-cell :width="4">{{ $t('common.actions') }}</sui-table-header-cell>
                </sui-table-row>
            </sui-table-header>
            <sui-table-body>
                <sui-table-row v-for="gatewayProcess in getComputedGatewayProcesses" :key="gatewayProcess.id" :warning="gatewayProcess.warning.length>0">
                    <sui-table-cell>
                        <div>
                            <sui-label :color="getCurrentStateColor(gatewayProcess.currentState)" ribbon>
                                {{ getCurrentStateLabel(gatewayProcess.currentState) }}
                            </sui-label>
                        </div>
                        <div>
                            <sui-label v-if="gatewayProcess.warning.length>0" ribbon color="orange" style="font-style: italic" :title="gatewayProcess.warning.join('\n')">
                              {{ $t('common.warning') }}
                             </sui-label>
                        </div>
                    </sui-table-cell>
                    <sui-table-cell>
                        {{ gatewayProcess.id }}
                    </sui-table-cell>
                    <sui-table-cell>
                        {{ gatewayProcess.relatedJobId }}
                        <sui-button-group basic>
                            <sui-button size="mini" compact @click="openArenaJson(gatewayProcess.relatedJobId)">Json
                            </sui-button>
                            <sui-button size="mini" compact @click="openArenaLink(gatewayProcess.relatedJobId)">Link
                            </sui-button>
                        </sui-button-group>
                    </sui-table-cell>
                    <sui-table-cell text-align="center">
                        <sui-label v-if="gatewayProcess.currentState !== 'ACTIVE'">
                            {{ getListingsCount(gatewayProcess) }}
                        </sui-label>
                        <sui-label v-else>...</sui-label>
                    </sui-table-cell>
                    <sui-table-cell v-if="gatewayProcess.startedAt">
                        {{ getDate(gatewayProcess.startedAt) }}
                        <br/>
                        ({{ getFromNow(gatewayProcess.startedAt) }})
                    </sui-table-cell>
                    <sui-table-cell v-else></sui-table-cell>
                    <sui-table-cell v-if="gatewayProcess.endedAt">
                        {{ getDate(gatewayProcess.endedAt) }}
                        <br/>
                        ({{ getFromNow(gatewayProcess.endedAt) }})
                    </sui-table-cell>
                    <sui-table-cell v-else></sui-table-cell>

                    <sui-table-cell v-if="gatewayProcess.startedAt && gatewayProcess.endedAt">
                        {{ getDiffDate(gatewayProcess.startedAt, gatewayProcess.endedAt) }}
                    </sui-table-cell>
                    <sui-table-cell v-else></sui-table-cell>

                    <sui-table-cell>
                        {{ getOriginLabel(gatewayProcess.origin) }}
                    </sui-table-cell>
                    <sui-table-cell text-align="center">
                        <sui-dropdown :text="$t('common.download')" button pointing direction="downward" :disabled="!gatewayProcess.backupFilename && !gatewayProcess.logFilename">
                            <sui-dropdown-menu>
                                <sui-dropdown-item
                                    :disabled="!gatewayProcess.backupFilename"
                                    @click="downloadFile(gatewayProcess.id, 'entrypoint')"
                                >
                                    {{ $t('gateway.processes.inputFile') }}
                                </sui-dropdown-item>
                                <sui-dropdown-item
                                    :disabled="!gatewayProcess.logFilename"
                                    @click="downloadFile(gatewayProcess.id, 'log')"
                                >
                                    {{ $t('common.logs') }}
                                </sui-dropdown-item>
                            </sui-dropdown-menu>
                        </sui-dropdown>
                        <sui-button
                            :disabled="isRestartButtonDisabled(gatewayProcess)"
                            :loading="isGatewayProcessRestarting"
                            @click="restartGatewayProcess(gatewayProcess.id)"
                        >
                            {{ $t('common.restart') }}
                        </sui-button>
                    </sui-table-cell>
                </sui-table-row>
            </sui-table-body>
       </sui-table>

       <GatewayProcessListings
            :hasCurrentRunningProcess="hasCurrentRunningProcess"
            :listings="listings"
            :gatewayProcessId="getLatestSuccessfulGatewayProcessId()"
        />
    </sui-segment>
</template>

<script>
import { api } from '@/api';
import store from '@/store';
import moment from 'moment';
import { processStatesColors, processStatesLabels } from '@/services/process-states';
import GatewayProcessListings from '@/components/gateway-file/GatewayProcessListings';
import {cancelAllRequests} from "@/helper/axiosUtils";

export default {
    name: 'GatewayProcessMonitoring',
    components: {
        GatewayProcessListings
    },
    async beforeMount() {
        this.isLoading = true;

        this.clearFetchDataInterval();

        await Promise.all([
            this.fetchMonitoringInfos(),
            this.fetchLastProcessSkipDate(),
            this.fetchGatewayProcesses(),
        ]);

        store.commit('setAppTitle', `${this.getCustomer().name} - ${this.monitoringInfos.gateway.name}`);

        this.isLoading = false;
        await this.fetchLatestSynchroStat()
    },
    beforeDestroy() {
        this.clearFetchDataInterval();
        store.commit('removeAppTitle');
    },
    data() {
        return {
            isUploadingFile: false,
            isLoading: true,
            monitoringInfos: null,
            lastProcessSkipDate: null,
            fetchGatewayProcessesInterval: null,
            isGatewayProcessRestarting: false,
            isSynchingToOdyssey: false,
            gatewayProcesses: [],
            listings: [],
            customerId: this.$route.params.customer_id,
            gatewayFileId: this.$route.params.gateway_file_id,
            latestSynchroStats: null,
            customerWithSynchedListings: [],
        }
    },
    mounted() {
        store.commit('setAppTitle', `${this.$t('common.gateway')}`);
    },
    computed: {
        hasCurrentRunningProcess() {
            return this.gatewayProcesses.filter(process => process.currentState === 'ACTIVE').length > 0;
        },
        baseURL() {
            return `${process.env.VUE_APP_API_URL}/monitoring-bff/v1/customers/${this.customerId}/gateway-files/${this.gatewayFileId}`;
        },
        isSharedGatewayFile() {
            return this.getCustomer().id !== this.monitoringInfos.customer.id;
        },
        getComputedGatewayProcesses() {
            const latestId = this.getLatestSuccessfulGatewayProcessId();

            return this.gatewayProcesses.map((gatewayProcess) => {
                const warning = [];

                if (latestId === gatewayProcess.id) {
                    const listingsCountMismatch =
                        Math.max(gatewayProcess.listingsCount, this.listings.length) -
                        Math.min(gatewayProcess.listingsCount, this.listings.length);

                    if (listingsCountMismatch > 0) {
                        warning.push(`${listingsCountMismatch} ${this.$t('gateway.processes.listingsCountMismatch')}`);
                    }

                    const listingsWithOneMedia = this.listings.filter(l => (l.medias_uris ?? []).length === 1);
                    if (listingsWithOneMedia.length > 0) {
                        warning.push(`${listingsWithOneMedia.length} ${this.$t('gateway.processes.listingsWithOneMedia')}`);
                    }

                    const listingsWithoutReference = this.listings.filter((listing) => !listing?.determined_id);
                    if (listingsWithoutReference.length > 0) {
                        warning.push(`${listingsWithoutReference.length} ${this.$t('gateway.processes.listingsWithoutReference')}`);
                    }
                }

                return { ...gatewayProcess, warning };
            });
        },
        lastProcessWithWarnings() {
            const processes = this.getComputedGatewayProcesses ?? []
            const sorted = [...processes].sort(
                (a, b) => new Date(b.startedAt ?? b.started_at ?? 0).getTime() - new Date(a.startedAt ?? a.started_at ?? 0).getTime()
            )
            return sorted.find(p => (p.warning?.length ?? 0) > 0) ?? null
        }
    },
    methods: {
        async fetchMonitoringInfos() {
            this.monitoringInfos = await api.gatewaysFiles.getInfos(this.customerId, this.gatewayFileId);
        },
        async fetchLastProcessSkipDate() {
            const date = await api.gatewaysFiles.getLastProcessSkipDate(this.customerId, this.gatewayFileId);
            this.lastProcessSkipDate = this.getDate(date);
        },
        async fetchGatewayProcesses() {
            const [gatewayProcessesResponse, listingsResponse] = await Promise.all([
                api.gatewaysFiles.getProcesses(this.customerId, this.gatewayFileId),
                api.gatewaysFiles.getListings(this.customerId, this.gatewayFileId),
                this.fetchLatestSynchroStat(),
            ]);

            this.gatewayProcesses = gatewayProcessesResponse;
            this.listings = listingsResponse;
            this.startFetchDataInterval();
        },
        async fetchLatestSynchroStat() {
            const latestGatewayProcessId = await this.getLatestSuccessfulGatewayProcessId()
            if (latestGatewayProcessId) {
                this.latestSynchroStats = await api.realEstateListing.getGatewayProcessStat(latestGatewayProcessId)
                if (this.latestSynchroStats?.distinctLegacyCompanyId?.length) {
                    const customerIds = this.latestSynchroStats?.distinctLegacyCompanyId.join(',');
                    const customers = await api.customers.fetch({ customerIds });
                    this.customerWithSynchedListings = customers.items.sort((a, b) => a.name.localeCompare(b.name));
                }
            }
        },
        async syncToOdyssey() {
            this.isSynchingToOdyssey = true;
            try {
                await api.gatewaysFiles.syncToOdyssey(this.gatewayFileId);
                this.$toastr.s(this.$t('gateway.synchro.reSyncOk'));
            } catch (error) {
                const message = (error.response && error.response.data && error.response.data.message)
                    ? error.response.data.message
                    : error.message;

                this.$toastr.e(`${this.$t('gateway.synchro.reSyncKO')}: ${message}`);
            } finally {
                this.isSynchingToOdyssey = false;
            }
        },
        async onClickDownloadFile() {
            const url = `${this.baseURL}/entrypoint`;
            window.open(url, '_blank');
        },
        getOriginLabel(origin) {
            const m = {
                AUTO: 'Automatique',
                MANUAL: 'Manuelle',
            };

            return m[origin];
        },
        getCustomer() {
            return [
                this.monitoringInfos.customer,
                ...this.monitoringInfos.sharedWith
            ].find(customer => customer.id === +this.customerId)
        },
        getListingsCount(gatewayProcess) {
            if (this.getLatestSuccessfulGatewayProcessId() === gatewayProcess.id) {
                return `${this.listings.length} / ${gatewayProcess.listingsCount}`;
            }
            return typeof gatewayProcess.listingsCount === 'number' ? gatewayProcess.listingsCount : 'N/C';
        },
        getLatestSuccessfulGatewayProcessId() {
            const idx = this.gatewayProcesses.findIndex(gp => gp.currentState === 'COMPLETED');

            return (idx > -1) ? this.gatewayProcesses[idx].id : null;
        },
        isRestartButtonDisabled(gatewayProcess) {
            return (
                ['TERMINATED', 'COMPLETED'].includes(gatewayProcess.currentState) === false ||
                this.hasCurrentRunningProcess ||
                this.isGatewayProcessRestarting ||
                !gatewayProcess.backupFilename
            );
        },
        startFetchDataInterval() {
            this.clearFetchDataInterval();
            let waitUntil = 60000;
            if (this.gatewayProcesses.some((gatewayProcess) => gatewayProcess.currentState === 'ACTIVE')) {
                waitUntil = 10000
            }
            this.fetchGatewayProcessesInterval = setInterval(async () => {
                await this.fetchGatewayProcesses();
            }, waitUntil);
        },
        clearFetchDataInterval() {
            if (this.fetchGatewayProcessesInterval) {
                clearInterval(this.fetchGatewayProcessesInterval);
            }
        },
        getCurrentStateLabel(currentState) {
            return processStatesLabels[currentState];
        },
        getCurrentStateColor(currentState) {
            return processStatesColors[currentState];
        },
        getDate(d) {
            if (!d) { return; }
            return moment(d).format('DD/MM/YYYY HH:mm:ss');
        },
        getFromNow(d) {
            if (!d) { return; }
            return moment(d).fromNow();
        },
        getDiffDate(startDate, endDate) {
            if (!startDate || !endDate) { return; }
            return moment.utc(moment(endDate).diff(moment(startDate))).format('HH:mm:ss');
        },
        onClickSelectFile() {
            this.$refs.inputFile.click();
        },
        async onSelectUploadFile(event) {
            try {
                this.isUploadingFile = true;
                await api.gatewaysFiles.uploadProcess(this.customerId, this.gatewayFileId, event.target.files[0]);
                await this.fetchGatewayProcesses();
                this.$toastr.s(this.$t('gateway.instructions.downloadSuccess'));
            } catch(error) {
                const message = (error.response && error.response.data && error.response.data.message)
                    ? error.response.data.message
                    : error.message;

                this.$toastr.e(`${this.$t('gateway.instructions.restartError')}: ${message}`);
            } finally {
                this.isUploadingFile = false;
                event.target.value = null;
            }
        },
        downloadFile(gatewayProcessId, fileType) {
            window.open(api.gatewaysFiles.getProcessDownloadUrl(gatewayProcessId, fileType), '_blank');
        },
        async restartGatewayProcess(gatewayProcessId) {
            this.isGatewayProcessRestarting = true;
            try {
                await api.gatewaysFiles.restartProcess(gatewayProcessId)
                await this.fetchGatewayProcesses();

                this.$toastr.s(this.$t('gateway.instructions.restartSuccess'));
            } catch (error) {
                const message = (error.response && error.response.data && error.response.data.message)
                    ? error.response.data.message
                    : error.message;

                this.$toastr.e(`${this.$t('gateway.instructions.restartError')}: ${message}`);
            } finally {
                this.isGatewayProcessRestarting = false;
            }
        },
      async onClickAbort() {
        try {
          cancelAllRequests();
          await api.gatewaysFiles.abort(this.customerId, this.gatewayFileId)
          await this.fetchGatewayProcesses();
          this.$toastr.s(this.$t('gateway.instructions.abortSuccess'));
        }
        catch(error) {
          const message = error?.response?.data?.message || error.message;
          this.$toastr.e(`${this.$t('gateway.instructions.abortError')}: ${message}`);
        }
      },
        openArenaJson(jobId) {
            window.open(`${process.env.VUE_APP_WORKER_URL}/monitoring/API/GATEWAY_PROCESS/${jobId}?json=true`, '_blank');
        },
        openArenaLink(jobId) {
            window.open(`${process.env.VUE_APP_WORKER_URL}/monitoring/API/GATEWAY_PROCESS/${jobId}`, '_blank');
        },
        goToCustomerParentGateway(customerId) {
            const routeData = this.$router.resolve({
                name: 'CustomerGatewaysList',
                params: {
                    customer_id: customerId,
                }
            });
            window.open(routeData.href, '_blank');
        },
        openGateway(gatewayId) {
            const routeData = this.$router.resolve({
                name: 'GatewayOverview',
                params: {
                    gateway_id: gatewayId,
                }
            });
            window.open(routeData.href, '_blank');
        },
        openCustomer(customerId) {
            const routeData = this.$router.resolve({
                name: 'CustomerGatewaysList',
                params: {
                    customer_id: customerId,
                }
            });
            window.open(routeData.href, '_blank');
        },
    }
}
</script>

<style>
.hoverable-message {
    cursor: pointer;
}
.muted { opacity: .7; font-weight: 400; margin-left: 6px; }

</style>
